import { useState } from 'react';
import { Box } from '@mui/material';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import colors from '../../../../assets/theme/base/colors';
import { PriorityRankModal } from './PriorityRankModal';

interface HeatMapLegendProps {
    cellWidth: number;
}

const severityLegendItems = [
    { Icon: DisabledByDefaultOutlinedIcon, bg: colors.deviationSeverity.critical.bg,        iconColor: colors.deviationSeverity.critical.icon,        label: 'Critical Severity - Relative gap exceeds 25% below target' },
    { Icon: WarningAmberOutlinedIcon,      bg: colors.deviationSeverity.high.bg,            iconColor: colors.deviationSeverity.high.icon,            label: 'High Severity - Relative gap between 15% and 25% below target' },
    { Icon: InfoOutlinedIcon,              bg: colors.deviationSeverity.medium.bg,          iconColor: colors.deviationSeverity.medium.icon,          label: 'Medium Severity - Relative gap between 5% and 15% below target' },
    { Icon: CheckCircleOutlinedIcon,       bg: colors.deviationSeverity.onTrack.bg,         iconColor: colors.deviationSeverity.onTrack.icon,         label: 'On Track - Relative gap within 5% of target' },
    { Icon: TrendingUpIcon,               bg: colors.deviationSeverity.overRepresented.bg, iconColor: colors.deviationSeverity.overRepresented.icon, label: 'Over-represented - Current headcount exceeds target, no severity applied' },
];

const cellFormatItems = [
    { sign: '-', label: 'Under-representation' },
    { sign: '+', label: 'Over-representation' },
    { sign: '0', label: 'On target' },
];

export function HeatMapLegend({ cellWidth }: HeatMapLegendProps) {
    const [showPriorityModal, setShowPriorityModal] = useState(false);

    return (
        <>
            <Box
                sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: colors.white.main,
                    borderRadius: '8px',
                    border: `1px solid ${colors.ui.border}`,
                }}
            >
                <Box sx={{ fontSize: '0.875rem', fontWeight: 700, color: colors.dark.main, mb: 1.5 }}>Legend</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>

                    <Box>
                        <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.ui.secondaryText, textTransform: 'uppercase', mb: 1.5 }}>
                            Cell Format
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: cellWidth,
                                    height: 80,
                                    backgroundColor: colors.ui.sampleCellBackground,
                                    borderRadius: '4px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.25,
                                    flexShrink: 0,
                                }}
                            >
                                <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: colors.ui.secondaryText, lineHeight: 1 }}>
                                    Variance
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: colors.ui.secondaryText, lineHeight: 1 }}>Current</Box>
                                    <Box component="span" sx={{ fontSize: '0.6875rem', color: colors.ui.tertiaryText, lineHeight: 1 }}>|</Box>
                                    <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: colors.ui.secondaryText, lineHeight: 1 }}>Target</Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {cellFormatItems.map(({ sign, label }) => (
                                    <Box key={sign} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 700, width: 16, textAlign: 'center', flexShrink: 0, color: colors.dark.main }}>
                                            {sign}
                                        </Box>
                                        <Box component="span" sx={{ fontSize: '0.875rem', color: colors.dark.main, lineHeight: 1 }}>{label}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.ui.secondaryText, textTransform: 'uppercase', mb: 1.5 }}>
                            Severity
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {severityLegendItems.map(({ Icon, bg, iconColor, label }) => (
                                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 40, height: 20, borderRadius: '4px', backgroundColor: bg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon sx={{ width: 14, height: 14, color: iconColor }} />
                                    </Box>
                                    <Box component="span" sx={{ fontSize: '0.75rem', color: colors.dark.main }}>{label}</Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.ui.secondaryText, textTransform: 'uppercase' }}>
                                Priority Rank
                            </Box>
                            <Box
                                component="button"
                                onClick={() => setShowPriorityModal(true)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: colors.ui.mutedText,
                                    p: 0,
                                    '&:hover': { color: colors.ui.activeBackground },
                                }}
                            >
                                <InfoOutlinedIcon sx={{ width: 14, height: 14 }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.dark.main, lineHeight: 1 }}>P{n}</Box>
                                    <Box component="span" sx={{ fontSize: '0.75rem', color: colors.ui.secondaryText, lineHeight: 1 }}>Priority {n}</Box>
                                </Box>
                            ))}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', gridColumn: '1 / -1' }}>
                                <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.dark.main, lineHeight: 1 }}>NP</Box>
                                <Box component="span" sx={{ fontSize: '0.75rem', color: colors.ui.secondaryText, lineHeight: 1 }}>Not Prioritized - Foreign Nationals</Box>
                            </Box>
                        </Box>
                        <Box sx={{ fontSize: '0.75rem', color: colors.ui.tertiaryText, mt: 1.5 }}>
                            Ranked per occupational level - P1 is the group furthest behind relative to its sectoral target.
                        </Box>
                    </Box>

                </Box>
            </Box>

            <PriorityRankModal open={showPriorityModal} onClose={() => setShowPriorityModal(false)} />
        </>
    );
}
